import { useEffect, useRef, useState } from 'react'
import { getAxiosInstance } from 'services/api'
import { useAuth } from 'store/auth/hooks'
import type { NextLeagueMatch } from 'views/Hub/data/useNextLeagueMatch'

const FAST_POLL_MS = 30 * 1000  // live / today: 30s
const SLOW_POLL_MS = 2 * 60 * 1000  // otherwise: 2min

const isUrgent = (m: NextLeagueMatch): boolean => {
  if (m.status === 'live') return true
  if (!m.scheduledTime) return false
  const t = new Date(m.scheduledTime).getTime()
  if (isNaN(t)) return false
  const sameDay = new Date(t).toDateString() === new Date().toDateString()
  return sameDay
}

/**
 * Compact data hook for the new UpcomingMatchesList widget. Mirrors the
 * adaptive polling behaviour of the original 309-line UpcomingMatchesWidget
 * (30s when anything is live/today, 2min otherwise) — minus all the rendering
 * baggage.
 */
export const useUpcomingMatches = (
  limit = 3,
): { matches: NextLeagueMatch[]; loading: boolean } => {
  const { isLoggedIn, user } = useAuth()
  const [matches, setMatches] = useState<NextLeagueMatch[]>([])
  const [loading, setLoading] = useState(false)
  const timerRef = useRef<number | null>(null)

  useEffect(() => {
    if (!isLoggedIn || !user?._id) {
      setMatches([])
      return
    }
    let cancelled = false
    const axios = getAxiosInstance()

    const fetchOnce = async () => {
      try {
        const res = await axios.get('/leagues/user/upcoming-matches', {
          params: { limit },
        })
        if (cancelled) return
        const next: NextLeagueMatch[] = res.data?.matches ?? []
        setMatches(next)
        const interval = next.some(isUrgent) ? FAST_POLL_MS : SLOW_POLL_MS
        scheduleNext(interval)
      } catch {
        if (!cancelled) setMatches([])
        scheduleNext(SLOW_POLL_MS)
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    const scheduleNext = (ms: number) => {
      if (cancelled) return
      timerRef.current = window.setTimeout(fetchOnce, ms)
    }

    setLoading(true)
    fetchOnce()

    return () => {
      cancelled = true
      if (timerRef.current != null) {
        window.clearTimeout(timerRef.current)
        timerRef.current = null
      }
    }
  }, [isLoggedIn, (user as any)?._id, limit])

  return { matches, loading }
}
